import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SearchableSelect.css';

/**
 * Native-feel select with type-to-search filtering.
 * options: [{ value, label }]
 */
const SearchableSelect = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  emptyLabel = 'No matches'
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const pick = (optValue) => {
    onChange?.(optValue);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={`searchable-select ${disabled ? 'is-disabled' : ''} ${open ? 'is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="searchable-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setQuery('');
        }}
      >
        <span className={selected ? 'searchable-select-value' : 'searchable-select-placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="searchable-select-caret" aria-hidden>▾</span>
      </button>

      {open && !disabled ? (
        <div className="searchable-select-panel" role="listbox">
          <input
            ref={inputRef}
            type="text"
            className="searchable-select-search"
            placeholder="Type to search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                setQuery('');
              }
              if (e.key === 'Enter' && filtered.length === 1) {
                e.preventDefault();
                pick(filtered[0].value);
              }
            }}
          />
          <div className="searchable-select-options">
            <button
              type="button"
              className={`searchable-select-option ${!value ? 'is-active' : ''}`}
              onClick={() => pick('')}
            >
              {placeholder}
            </button>
            {filtered.length === 0 ? (
              <div className="searchable-select-empty">{emptyLabel}</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="option"
                  aria-selected={String(opt.value) === String(value)}
                  className={`searchable-select-option ${String(opt.value) === String(value) ? 'is-active' : ''}`}
                  onClick={() => pick(opt.value)}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SearchableSelect;
